<template>
  <AppLayout>
    <div v-if="loading" class="dashboard-loading">
      <div class="loading-spinner-large"></div>
      <p class="loading-text">{{ t('common.loading') }}</p>
    </div>
    <template v-else>
      <div class="project-layout">        
        <!-- Main Content Area -->        
        <div class="project-content">
          <!-- Quick Actions Tabs - only render when we have a selected project id -->
          <QuickActionsTabs
            v-if="selectedProject && selectedProject.id !== undefined && selectedProject.id !== null"
            :tabs="quickActionTabs"
            :activeTab="activeComponent"
            :projectId="selectedProject.id"
            :maxPrimaryTabs="5"
            :enableGrouping="true"
            @select="setActiveComponent"
          />
          
          <!-- Remove AppCard wrapper for mobile optimization -->
          <div class="project-main-content">
            <div v-if="!selectedProject" class="no-project-selected">
                <div class="no-project-illustration">
                  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19Z"
                      stroke="var(--neutral-300)" stroke-width="2" />
                    <path d="M9 9H15" stroke="var(--neutral-300)" stroke-width="2" stroke-linecap="round" />
                    <path d="M9 13H15" stroke="var(--neutral-300)" stroke-width="2" stroke-linecap="round" />
                    <path d="M9 17H13" stroke="var(--neutral-300)" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </div>
                <h3 class="no-project-title">{{ t('projects.noProjectTitle') }}</h3>
                <p class="no-project-message">{{ t('projects.selectProject') }}</p>
              </div>              <div v-else>
                <div v-if="activeComponent === 'info'">
                  <!-- Show project info when Info tab is active -->
                  <ProjectInfo :project="selectedProject" :original-project="originalValues"
                    @update:project="updateProject" @saved="handleProjectSaved" />
                </div>
                <div v-else-if="activeComponent">
                  <!-- Show dynamic content based on active component -->
                  <DynamicContent :component-name="activeComponent" :project-id="selectedProject?.id" :project="selectedProject"
                    @retry="handleDynamicContentRetry" @return-to-project="returnToProject" />
                </div>
                <div v-else>
                  <!-- Redirect to info tab if no component is active -->
                  <ProjectInfo :project="selectedProject" :original-project="originalValues"
                    @update:project="updateProject" @saved="handleProjectSaved" />
                </div>              </div>
            </div>
        </div>
      </div>
    </template>
  </AppLayout>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from '@/composables/useStore'
import { useNotifications } from '@/composables/useNotifications'
import { useAuthStore } from '@/stores'
import type { Project } from '@/types'

// Components
import AppLayout from '../base/AppLayout.vue'
import QuickActionsTabs from './QuickActionsTabs.vue'
import ProjectService from '../../services/project.service'
import { ProjectInfo, DynamicContent, ProjectAuditTrail } from './index'

// Composables
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useStore()
const authStore = useAuthStore()
const { notifySuccess, notifyError } = useNotifications()

// Determine if the current user is a CLIENT
const isClientUser = computed(() => authStore.user?.type === 'CLIENT')

// Reactive state
const loading = ref<boolean>(false)
const selectedProject = ref<Project | null>(null)
const originalValues = ref<Project>({} as Project)
const activeComponent = ref<string | null>(null)

// Types for quick action tabs
interface QuickActionTab {
  name: string
  label: string
  title: string
  icon: string
  permission?: string | string[]
  permissions?: string[]
  priority?: 'primary' | 'secondary'
  category?: string
  badge?: string | number
}

// Load project data
const loadProjects = async (): Promise<void> => {
  try {
    loading.value = true
    const projectId = route.params.projectId as string

    if (projectId) {
      const projectData = await ProjectService.getProjectById(projectId)

      if (!projectData) {
        throw new Error('Project data is empty or null')
      }

      selectedProject.value = projectData
      originalValues.value = { ...projectData } // Store original values after loading

      // Update store with current project data for breadcrumb
      store.commit('SET_CURRENT_PROJECT', projectData)

      // Only set to 'info' if no other component is already active or requested via route
      if (!activeComponent.value && !route.query.component) {
        setActiveComponent('info')
      }
    } else {
      // Handle the case where no projectId is provided
      selectedProject.value = null
      originalValues.value = {} as Project

      // Clear current project from store
      store.commit('CLEAR_CURRENT_PROJECT')
    }
  } catch (error) {
    notifyError(t('projects.errorLoadingProject'))
    selectedProject.value = null

    // Clear current project from store on error
    store.commit('CLEAR_CURRENT_PROJECT')
  } finally {
    loading.value = false
  }
}

// Lifecycle hooks
onMounted(() => {
  if (route.params.projectId) {
    loadProjects().then(() => {
      // Check if there's a component to activate from the query parameter
      const componentFromQuery = route.query.component as string
      if (componentFromQuery) {
        setActiveComponent(componentFromQuery)
      } else {
        // Set 'info' as the default active tab when a project is loaded
        setActiveComponent('info')
      }
    })
  }
})

// Watch for route query changes to update active component
watch(
  () => route.query.component,
  (newComponent: string | string[] | undefined) => {
    if (newComponent && typeof newComponent === 'string') {
      activeComponent.value = newComponent
    }
  }
)

// Clean up store when component is unmounted
onUnmounted(() => {
  // Clear current project from store when leaving the component
  store.commit('CLEAR_CURRENT_PROJECT')
})

// Set the active component and update the URL
const setActiveComponent = (componentName: string): void => {
  activeComponent.value = componentName
  
  // Update URL query parameter without reloading the page
  if (route.query.component !== componentName) {
    router.push({
      name: 'ProjectDetails',
      params: { projectId: route.params.projectId },
      query: { component: componentName }
    })
  }
}

// Navigate to the project info tab
const returnToProject = (): void => {
  setActiveComponent('info')
}

// Handle retry for dynamic content
const handleDynamicContentRetry = (): void => {
  // Implementation would depend on the specific component
  // Retry loading content for the active component
}

// Update project data
const updateProject = (newProjectData: Project): void => {
  selectedProject.value = { ...newProjectData }
}

// Handle project saved event from ProjectInfo component
const handleProjectSaved = (savedProject: Project): void => {
  originalValues.value = { ...savedProject }
  notifySuccess(t('projects.changesSaved'))
}

// Make quickActionTabs reactive to language changes
const quickActionTabs = computed((): QuickActionTab[] => {
  const baseTabs: QuickActionTab[] = [
    {
      name: 'info',
      label: t('projects.projectInfo'),
      title: t('projects.projectInfo'),
      icon: `<span style="font-size: 1.2em;">ℹ️</span>`,
      permission: 'VIEW_PROJECT_INFO',
      priority: 'primary'
    },
    {
      name: 'planning',
      label: t('projects.planningAction'),
      title: t('projects.planningAction'),
      icon: `<span style="font-size: 1.2em;">📅</span>`,
      permissions: ['ACCESS_PHASES', 'ACCESS_TASKS'],
      priority: 'primary',
      category: 'management'
    },
    {
      name: 'photos',
      label: t('projects.photosAction'),
      title: t('projects.photosAction'),
      icon: `<span style="font-size: 1.2em;">📷</span>`,
      permission: 'ACCESS_PHOTO',
      priority: 'primary'
    },
    {
      name: 'documents',
      label: t('projects.documentsAction'),
      title: t('projects.documentsAction'),
      icon: `<span style="font-size: 1.2em;">📄</span>`,
      permission: 'ACCESS_DOCUMENT',
      priority: 'primary'
    },
    {
      name: 'workspace',
      label: t('projects.workspaceAction'),
      title: t('projects.workspaceAction'),
      icon: `<span style="font-size: 1.2em;">🏗️</span>`,
      permissions: ['ACCESS_PHASES', 'ACCESS_TASKS', 'ACCESS_INTERVENANTS'],
      priority: 'primary',
      category: 'management'
    },
    {
      name: 'budget',
      label: t('projects.budgetAction'),
      title: t('projects.budgetAction'),
      icon: `<span style="font-size: 1.2em;">💰</span>`,
      permission: 'ACCESS_FINANCE',
      priority: 'primary',
      category: 'finance'
    },
    {
    name: 'access',
      label: t('projects.accessAction'),
      title: t('projects.accessAction'),
      icon: `<span style="font-size: 1.2em;">🔒</span>`,
      permission: 'MANAGE_PROJECT_ACCESS',
      priority: 'secondary',
      category: 'settings'
    },
    {
      name: 'design',
      label: t('projects.designAction'),
      title: t('projects.designAction'),
      icon: `<span style="font-size: 1.2em;">📐</span>`,
      permission: 'ACCESS_3D_DESIGN',
      priority: 'secondary',
      category: 'design'
    },
    {
      name: 'notes',
      label: t('projects.notesAction'),
      title: t('projects.notesAction'),
      icon: `<span style="font-size: 1.2em;">📝</span>`,
      permission: 'NOTES',
      priority: 'secondary',
      category: 'communication'
    },
    {
      name: 'report',
      label: t('projects.viewReport'),
      title: t('projects.viewReport'),
      icon: `<span style="font-size: 1.2em;">📈</span>`,
      permission: 'VIEW_REPORTS',
      priority: 'secondary',
      category: 'reporting'
    },
    {
      name: 'issues',
      label: t('projects.issuesAction'),
      title: t('projects.issuesAction'),
      icon: `<span style="font-size: 1.2em;">⚠️</span>`,
      permission: 'ACCESS_ISSUES',
      priority: 'secondary',
      category: 'delivery'
    },
    {
      name: 'notifications',
      label: t('projects.notificationsAction'),
      title: t('projects.notificationsAction'),
      icon: `<span style="font-size: 1.2em;">🔔</span>`,
      priority: 'secondary',
      category: 'notifications'
    },
    {
      name: 'audit',
      label: t('projects.auditTrail'),
      title: t('projects.auditTrail'),
      icon: `<span style="font-size: 1.2em;">📋</span>`,
      permission: 'VIEW_PROJECT_AUDIT',
      priority: 'secondary',
      category: 'audit'
    }
  ]

  // In FULL_MANAGEMENT projects the client must not see finance or reports
  const isFullManagement = selectedProject.value?.managementType === 'FULL_MANAGEMENT'
  if (isClientUser.value && isFullManagement) {
    return baseTabs.filter(tab => tab.name !== 'budget' && tab.name !== 'report')
  }

  return baseTabs
})
</script>

<style scoped>
/* General Dashboard Styles */
.dashboard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80vh;
}

.loading-spinner-large {
  width: 60px;
  height: 60px;
  border: 6px solid var(--neutral-200);
  border-left-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-lg);
}

.loading-text {
  font-size: var(--font-size-lg);
  color: var(--neutral-700);
}

/* No Project Selected State */
.no-project-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.no-project-illustration {
  margin-bottom: 1.5rem;
}

.no-project-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.no-project-message {
  color: var(--text-secondary);
}

/* Project Layout */
.project-layout {
  display: block;
  min-height: calc(100vh - 40px); /* Match the top bar height used in AppLayout */
}

/* Project Content Area */
.project-content {
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  overflow-y: auto;
}

/* Enhanced content container styles */
.content-container {
  padding: 1.5rem;
  background: var(--bg-content, #fff);
  border-radius: var(--border-radius-md);
  margin-top: 0.5rem;
  box-shadow: inset 0 0 0 1px var(--neutral-200);
}

/* Tabs container */
.tabs-container {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

/* Return button styling removed as it's no longer needed */

/* Toast styles */
.toast-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
}

.toast {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-lg);
  padding: 1rem;
  width: 300px;
  animation: slideIn 0.3s ease-out forwards;
}

.toast--success {
  border-left: 4px solid var(--success);
}

.toast--error {
  border-left: 4px solid var(--danger);
}

.toast__icon {
  margin-right: 0.75rem;
}

.toast--success .toast__icon {
  color: var(--success);
}

.toast--error .toast__icon {
  color: var(--danger);
}

.toast__content {
  flex: 1;
  color: var(--text-primary);
}

.toast__close {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.project-content {
  padding: var(--space-lg);
  padding-top: calc(var(--quick-actions-height, 54px) + 15px); /* Minimal space after tabs */
}

.project-main-content {
  background: white;
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color, #e5e7eb);
  box-shadow: var(--shadow-md);
  padding: var(--space-md); /* Reduce from var(--space-lg) */
  min-height: 400px;
}

/* Remove old tabs container styles since tabs are now outside card */
.tabs-container {
  display: none; /* Hide the old tabs container */
}

/* Media queries for responsiveness */
@media (max-width: 992px) {
  .project-layout {
    display: block;
  }  .project-content {
    padding: var(--space-md);
    padding-top: calc(var(--quick-actions-height-mobile, 28px) + 2px);
  }
}

@media (max-width: 768px) {  .project-content {
    padding: var(--space-sm);
    padding-top: calc(var(--quick-actions-height-mobile, 28px) + 2px);
  }
    .project-main-content {
    padding: var(--space-xs); /* Reduce mobile padding further */
    border-radius: var(--border-radius-md);
    box-shadow: var(--shadow-sm);
  }
}

@media (max-width: 480px) {  .project-content {
    padding: var(--space-xs);
    padding-top: calc(var(--quick-actions-height-mobile, 24px) + 1px);
  }
    .project-main-content {
    padding: 4px; /* Minimal padding for very small screens */
    border-radius: 8px;
    box-shadow: none;
    border: 1px solid #e5e7eb;
  }
}
</style>
